import { formatDate } from "@sembark-travel/datetime-utils"
import { TMedia } from "../Media"
import { TTripDestination } from "../TripDestinations"
import { IUser } from "../Auth"
import { isTicketTypeClosedOnDate } from "./utils"
import { TTransportServiceLocationPoint } from "../TransportServices"

export type TTravelActivity = {
	id: number
	name: string
	ticket_types: Array<TTravelActivityTicketType>
	description?: string
	ticket_tourist_configurations: Array<TTravelActivityTicketTouristConfiguration>
	complimentary_till_age?: number
	required_minimum_age?: number
	allowed_maximum_age?: number
	media_url?: string | null
	thumb_image_url?: string | null
	latest_media?: TMedia
	trip_destinations?: Array<TTripDestination>
	created_at: string
	created_by?: IUser
	updated_at?: string
	updated_by?: IUser
	deleted_at?: string
	deleted_by?: IUser
	same_closing_days_and_intervals_for_all_tickets?: boolean
	/** If all ticket types have same closed days and date intervals */
	closed_on_days_of_week?: TTravelActivityTicketType["closed_on_days_or_dates"]
	/** If all ticket types have same closed days and date intervals */
	closed_on_date_intervals?: TTravelActivityTicketType["closed_on_date_intervals"]
	entry_type?: TTravelActivityTicketType["entry_type"]
	rate_expiry_date?: TTravelActivityTicketType["rate_expiry_date"]
	rate_expiry_date_local?: TTravelActivityTicketType["rate_expiry_date_local"]
	rate_uploaded_by?: TTravelActivityTicketType["rate_uploaded_by"]
}

export type TTravelActivityTicketType = {
	id: number
	activity_id: number
	name: string
	display_name: string
	short_name?: string
	description?: string
	durations?: Array<TTravelActivityDuration>
	opens_at?: string
	closes_at?: string
	closed_on_days_of_week?: Array<
		| "Sunday"
		| "Monday"
		| "Tuesday"
		| "Wednesday"
		| "Thursday"
		| "Friday"
		| "Saturday"
	>
	closed_on_days_or_dates?: Array<string>
	closed_on_date_intervals?: Array<{
		id: string
		name: string
		start_date: string
		end_date: string
	}>
	deleted_at?: string | null
	media_url?: string | null
	thumb_image_url?: string | null
	latest_media?: TMedia
	created_at: string
	created_by?: IUser
	updated_at?: string
	updated_by?: IUser
	entry_type?: "manually" | "by_sheet" | "by_preload" | "ai_generated"
	rate_expiry_date?: string
	rate_expiry_date_local?: string | null
	deleted_by?: IUser
	rate_uploaded_by?: IUser
	pick_up_locations?: Array<string | TTransportServiceLocationPoint>
	drop_locations?: Array<string | TTransportServiceLocationPoint>
	set_hotel_as_default_pickup?: string | null
	set_hotel_as_default_drop?: string | null
}

export type TTravelActivityDuration = {
	name?: string
	iso?: string
	slots?: Array<string>
}

export type TTravelActivityTicketTouristConfiguration = {
	id: number
	name: string
	tourist_type: "adult" | "child" | "group"
	age_start: number | null
	age_end: number | null
	quantity: number
}

export function getClosedService<
	T extends Array<{
		dates?: Array<Date>
		services?: Array<
			| {
					type: "transport_service"
					service: {
						transport_service?: {
							closed_on_days_or_dates?: TTravelActivityTicketType["closed_on_days_or_dates"]
							closed_on_days_of_week?: TTravelActivityTicketType["closed_on_days_of_week"]
							service: string
						}
					}
			  }
			| {
					type: "travel_activity"
					service: {
						ticket_type?: {
							closed_on_days_or_dates?: TTravelActivityTicketType["closed_on_days_or_dates"]
							closed_on_days_of_week?: TTravelActivityTicketType["closed_on_days_of_week"]
							name: string
						}
					}
			  }
		>
	}>,
>(daywise_services: T): Record<string, Date[]> {
	const closeData: Record<string, Date[]> = {}

	const mergeCloseData = (data: Record<string, Date[]>) => {
		for (const key in data) {
			closeData[key] = [...(closeData[key] || []), ...data[key]]
		}
	}

	for (const { services, dates } of daywise_services) {
		if (!services || !dates) continue

		for (const { type, service } of services) {
			if (type === "transport_service" && service.transport_service) {
				mergeCloseData(
					getClosedDates(
						service.transport_service,
						dates,
						service.transport_service.service
					)
				)
			} else if (type === "travel_activity" && service.ticket_type) {
				mergeCloseData(
					getClosedDates(service.ticket_type, dates, service.ticket_type.name)
				)
			}
		}
	}

	return closeData
}

export function getClosedDates(
	data: {
		closed_on_days_or_dates?: TTravelActivityTicketType["closed_on_days_or_dates"]
		closed_on_days_of_week?: TTravelActivityTicketType["closed_on_days_of_week"]
	},
	dates: Date[],
	label: string
): Record<string, Date[]> {
	const result: Record<string, Date[]> = {}
	for (const date of dates) {
		if (isTicketTypeClosedOnDate(data, date)) {
			if (!result[label]) result[label] = []
			result[label].push(date)
		}
	}
	return result
}

export function showClosedServicesAlert(closeData: Record<string, Date[]>) {
	const alertMessage = Object.entries(closeData)
		.map(([service, dates]) => {
			const sortedDates = [...dates].sort((a, b) => +a - +b)
			const formattedDates = sortedDates
				.map((date) => `• ${formatDate(date, "ddd, D MMM")}`)
				.join("\n   ")
			return ` ${service.toUpperCase()}:\n   ${formattedDates}`
		})
		.join("\n\n")
	return alertMessage
}
